/* import __COLOCATED_TEMPLATE__ from './plan-editor.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import type { ExternalPlanWireFormat } from 'embercom/objects/inbox/external-plan';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'ember-intl/services/intl';
import type PlansApi from 'embercom/services/plans-api';

interface Signature {
  Args: {
    predicate: $TSFixMe;
    changePredicateValue: (value: string) => void;
    changePredicateComparison: (value: string) => void;
  };
}

export default class PlanEditor extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare plansApi: PlansApi;

  @tracked emberPlans: ExternalPlanWireFormat[] = [];
  @tracked searchResults: ExternalPlanWireFormat[] = [];
  @tracked selectedPlan: ExternalPlanWireFormat | null = null;
  @tracked searchInput = '';
  @tracked isSearching = false;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    this.emberPlans = this.appService.app?.plans || [];
    this.searchResults = this.emberPlans.slice(0, 1000);
    this.loadSelectedPlanIfNeeded();
  }

  get dropdownOptions() {
    return this.emberPlans.map((plan) => ({
      text: plan.name,
      value: plan.id,
    }));
  }

  get app() {
    return this.appService.app;
  }

  get shouldAllowApiSearch() {
    return this.app.canUsePlanEditorSearch;
  }

  get selectedPlanName() {
    if (this.selectedPlan) {
      return this.selectedPlan.name;
    }

    return this.emberPlans.firstObject?.name;
  }

  async loadSelectedPlanIfNeeded() {
    if (!this.args.predicate.value) {
      return;
    }

    let existingPlan = this.emberPlans.find((plan) => plan.id === this.args.predicate.value);
    if (existingPlan) {
      this.selectedPlan = existingPlan;
    } else {
      let plan = await this.plansApi.findSelectedPlan(this.args.predicate.value);
      this.selectedPlan = plan;
    }
  }

  async searchPlans(inputText: string) {
    try {
      let response = await this.plansApi.searchPlans(inputText);
      this.searchResults = response || [];
    } catch (error) {
      captureException(error);
      this.searchResults = this.emberPlans.slice(0, 1000);
    } finally {
      this.isSearching = false;
    }
    return this.searchResults;
  }

  @action
  onSearchInput(event: KeyboardEvent & { target?: HTMLInputElement }) {
    let input = event.target?.value || '';
    this.searchInput = input;

    if (!input.trim()) {
      this.isSearching = false;
      this.searchResults = this.emberPlans.slice(0, 1000);
      return;
    }

    this.isSearching = true;
    debounce(this, this.searchPlans, input, 500);
  }

  @action
  selectPlan(plan: ExternalPlanWireFormat) {
    this.selectedPlan = plan;
    this.args.changePredicateValue(plan.id);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Predicates::Editors::PlanEditor': typeof PlanEditor;
  }
}
